<template>
  <section
    ref="myAssets"
    class="my-assets"
    :class="{ 'my-assets--three-columns': assetColumnCount === 3 }"
  >
    <header class="my-assets__header">
      <h1>{{ $t('dashboard.my_assets') }}</h1>

      <div class="my-assets__actions">
        <el-button type="primary" class="action-button withdraw-button" @click="onClickWithDrawBtn">
          {{ $t('dashboard.withdraw_now') }}
        </el-button>
        <el-button class="action-button" @click="onClickRechargeBtn">
          {{ $t('merchantAccount.top_up') }}
        </el-button>
        <el-button class="action-button detail-button" @click="$router.push({ name: 'Financial_Manage_Balance_Detail' })">
          {{ $t('data.balance_details') }}
        </el-button>
      </div>
    </header>

    <div class="assets-heading">
      <span>{{ $t('dashboard.currency_assets') }}</span>
      <div class="assets-heading__line" />
    </div>

    <el-skeleton style="width: 100%" :loading="loading" animated>
      <div>
        <div class="asset-grid">
          <article
            v-for="asset in visibleAssets"
            :key="asset.merchant.currency"
            class="asset-card"
            :class="{ 'asset-card--active': asset.active }"
          >
            <div class="asset-card__summary">
              <span class="asset-card__currency">{{ asset.merchant.currency }}</span>
              <span class="asset-card__total">{{ asset.balance.sum_balance | toDivideWithDot }}</span>
            </div>

            <div class="asset-card__divider" />

            <dl class="asset-card__details">
              <div class="asset-detail-row">
                <dt>
                  {{ $t('dashboard.available') }}
                  <el-tooltip placement="top">
                    <div slot="content" style="width: 300px">
                      <span>{{ $t('tips.dashboard_balance_question') }}</span>
                    </div>
                    <span> <svg-icon icon-class="icon-font-question" class="icon-question" /></span>
                  </el-tooltip>
                </dt>
                <dd>
                  <span>{{ asset.merchant.currency }}</span>
                  <strong>{{ asset.balance.balance | toDivideWithDot }}</strong>
                  <div class="available-box">
                    <el-tooltip placement="bottom">
                      <div slot="content">
                        <div>
                          <span>{{ $t('tips.dashboard_balance_number') }}  {{ asset.balance.available_withdraw | toDivideWithDot }} {{ asset.merchant.currency }}</span>
                        </div>
                      </div>
                      <span>  <svg-icon icon-class="icon-font-warning" class="icon-warning" /></span>
                    </el-tooltip>
                  </div>
                </dd>
              </div>
              <div class="asset-detail-row">
                <dt>{{ $t('dashboard.in_transit') }}</dt>
                <dd>
                  <span>{{ asset.merchant.currency }}</span>
                  <strong>{{ asset.balance.fund_settlement_amount | toDivideWithDot }}</strong>
                </dd>
              </div>
              <div class="asset-detail-row">
                <dt>{{ $t('dashboard.frozen') }}</dt>
                <dd>
                  <span>{{ asset.merchant.currency }}</span>
                  <strong>{{ asset.balance.adb_amount | toDivideWithDot }}</strong>
                </dd>
              </div>
            </dl>
          </article>
        </div>
      </div>
    </el-skeleton>

    <el-button
      v-if="assets.length > defaultVisibleCount"
      class="expand-button"
      round
      @click="expanded = !expanded"
    >
      {{ expanded ? $t('dashboard.collapse') : $t('dashboard.expand_more') }}
      <i :class="expanded ? 'el-icon-caret-top' : 'el-icon-caret-bottom'" />
    </el-button>

    <MultiMchWithdrawModal
      ref="withdrawModalRef"
      @get-merchant-info="getMchMultiInfo"
    />
  </section>
</template>

<script>
import { fetchMchMultiInfo } from '@/api/dashboard/dashboard'

export default {
  name: 'MyAssets',
  components: {
    MultiMchWithdrawModal: () => import('../Modal/MultiMchWithdrawModal.vue')
  },
  data() {
    return {
      assets: [],
      defaultVisibleCount: 4,
      expanded: false,
      assetColumnCount: 4,
      assetResizeObserver: null,
      loading: false
    }
  },
  computed: {
    visibleAssets() {
      return this.expanded ? this.assets : this.assets.slice(0, this.defaultVisibleCount)
    }
  },
  created() {
    this.getMchMultiInfo()
  },
  mounted() {
    this.updateAssetColumnCount()

    if (window.ResizeObserver) {
      this.assetResizeObserver = new window.ResizeObserver(entries => {
        if (entries.length) {
          this.updateAssetColumnCount(entries[0].contentRect.width)
        }
      })
      this.assetResizeObserver.observe(this.$refs.myAssets)
    } else {
      window.addEventListener('resize', this.updateAssetColumnCount)
    }
  },
  beforeDestroy() {
    if (this.assetResizeObserver) {
      this.assetResizeObserver.disconnect()
    } else {
      window.removeEventListener('resize', this.updateAssetColumnCount)
    }
  },
  methods: {
    getMchMultiInfo() {
      this.loading = true
      return fetchMchMultiInfo().then(res => {
        const { mch_info_resp_list = [] } = res.data || {}
        this.assets = mch_info_resp_list
      }).finally(() => {
        this.loading = false
      })
    },
    onClickWithDrawBtn() {
      this.$refs.withdrawModalRef.changModalStatus(true)
    },
    onClickRechargeBtn() {
      this.$router.push({ name: 'Financial_Manage_Balance_Recharge' })
    },
    updateAssetColumnCount(width) {
      const containerWidth = typeof width === 'number'
        ? width
        : this.$refs.myAssets.getBoundingClientRect().width
      const nextColumnCount = containerWidth < 1090 ? 3 : 4

      if (nextColumnCount !== this.assetColumnCount) {
        this.assetColumnCount = nextColumnCount
        this.defaultVisibleCount = nextColumnCount
        this.expanded = false
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.my-assets {
  box-sizing: border-box;
  width: 100%;
  padding: 14px 12px 10px;
  color: #0c1c35;
  background: #fff;
}

.my-assets__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
}

.my-assets__header h1 {
  margin: 0;
  font-size: 22px;
  font-weight: 700;
  line-height: 32px;
}

.my-assets__actions {
  display: flex;
  align-items: center;
  gap: 14px;
}

.action-button.el-button {
  min-width: 82px;
  height: 36px;
  margin-left: 0;
  padding: 0 18px;
  border-color: #dbe7fb;
  border-radius: 18px;
  color: #2f73e8;
  background: #fff;
  font-size: 13px;
  font-weight: 600;
}

.action-button.withdraw-button.el-button {
  min-width: 112px;
  border-color: #1769f6;
  color: #fff;
  background: #1769f6;
  box-shadow: 0 5px 12px rgba(23, 105, 246, 0.25);
}

.action-button.detail-button.el-button {
  min-width: 128px;
  color: #607794;
  border-color: #e2e8f1;
}

.assets-heading {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 16px;
  color: #5e7390;
  font-size: 13px;
  font-weight: 600;
}

.assets-heading__line {
  flex: 1;
  height: 1px;
  background: #e8eef7;
}

.asset-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 18px;
}

.asset-card {
  box-sizing: border-box;
  min-width: 0;
  padding: 20px 22px 17px;
  border: 1px solid #edf1f7;
  border-radius: 16px;
  background: #f8faff;
}

.asset-card--active {
  border-color: #dbe9ff;
  background: #fff;
  box-shadow: 0 2px 8px rgba(31, 96, 202, 0.03);
}

.asset-card__summary {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
}

.asset-card__currency {
  color: #284f83;
  font-size: 16px;
  font-weight: 600;
}

.asset-card__total {
  overflow: hidden;
  color: #07162d;
  font-size: 22px;
  font-weight: 700;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.asset-card__divider {
  height: 2px;
  margin: 14px 0 9px;
  background: #e4ebf6;
}

.asset-card__details {
  margin: 0;
}

.asset-detail-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 31px;
  border-bottom: 1px solid #edf1f6;
  position: relative;
  .available-box {
    position: absolute;
    right: -21px;
    top: 50%;
    transform: translateY(-50%);
  }
}

.asset-detail-row:last-child {
  border-bottom: 0;
}

.asset-detail-row dt,
.asset-detail-row dd {
  margin: 0;
}

.asset-detail-row dt {
  color: #627997;
  font-size: 12px;
  font-weight: 500;
}

.asset-detail-row dd {
  display: flex;
  align-items: baseline;
  gap: 4px;
  min-width: 0;
  color: #5c7698;
  font-size: 11px;
}

.asset-detail-row strong {
  overflow: hidden;
  color: #0c1c35;
  font-size: 14px;
  font-weight: 600;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.icon-question {
  font-size: 11px;
  position: relative;
  top: -2px;
  cursor: pointer;
  flex-shrink: 0;
}

.icon-warning {
  font-size: 15px;
  position: relative;
  top: -1px;
  cursor: pointer;
  flex-shrink: 0;
}

.expand-button.el-button {
  display: block;
  min-width: 108px;
  height: 34px;
  margin: 20px auto 0;
  padding: 0 18px;
  color: #607794;
  border-color: #e1e8f2;
  background: #fff;
  font-size: 12px;
  font-weight: 600;
}

.expand-button i {
  margin-left: 5px;
  font-size: 9px;
}

.my-assets.my-assets--three-columns .asset-grid {
  grid-template-columns: repeat(3, minmax(0, 1fr));
}

@media (max-width: 760px) {
  .my-assets__header {
    align-items: flex-start;
    flex-direction: column;
    gap: 18px;
  }

  .my-assets__actions {
    flex-wrap: wrap;
    gap: 10px;
  }

}
</style>
